import { useEffect, useRef, useMemo, ComponentType } from 'react';
import { useLocation } from 'wouter';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

type UserRole = 'PROSPECT' | 'BUYER' | 'SUPPLIER' | 'DISTRIBUTION_CENTER' | 'SOHNEN_ADMIN' | 'SHOPJIMMY_ADMIN' | 'SUPER_ADMIN';

interface RoleGuardConfig {
  allowedRoles?: UserRole[];
  requireAdmin?: boolean;
}

function checkRoles(user: any, config: RoleGuardConfig): boolean {
  if (!user) return false;
  const userRoles = user.roles || [];

  if (config.requireAdmin) {
    if (!userRoles.some((role: string) =>
      ['SOHNEN_ADMIN', 'SHOPJIMMY_ADMIN', 'SUPER_ADMIN'].includes(role)
    )) return false;
  }

  if (config.allowedRoles && config.allowedRoles.length > 0) {
    if (!userRoles.some((role: string) =>
      config.allowedRoles!.includes(role as UserRole)
    )) return false;
  }

  return true;
}

export function withRoleGuard<P extends object>(
  Component: ComponentType<P>,
  config: RoleGuardConfig = {}
) {
  return function RoleGuardedComponent(props: P) {
    const redirectedRef = useRef(false);
    const { user, isAuthenticated, isLoading } = useAuth();
    const [, setLocation] = useLocation();
    const { toast } = useToast();

    const hasAccess = useMemo(() => {
      if (isLoading) return false;
      if (!isAuthenticated || !user) return false;
      return checkRoles(user, config);
    }, [isLoading, isAuthenticated, user, config]);

    useEffect(() => {
      if (isLoading) return;
      if (redirectedRef.current) return;

      if (!isAuthenticated || !user) {
        redirectedRef.current = true;
        sessionStorage.setItem('auth_redirect', window.location.pathname + window.location.search);
        window.location.href = '/';
        return;
      }

      if (!checkRoles(user, config)) {
        redirectedRef.current = true;
        toast({
          title: "Access Denied",
          description: "You don't have permission to access this page.",
          variant: "destructive",
        });
        setLocation('/');
      }
    }, [isLoading, isAuthenticated, user, config, setLocation, toast]);

    if (!hasAccess) {
      if (isLoading) {
        return (
          <div className="flex items-center justify-center min-h-screen" data-testid="role-guard-loading">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Checking permissions...</p>
            </div>
          </div>
        );
      }
      return null;
    }

    return <Component {...props} />;
  };
}
